// @ts-check
import { button } from "./button.js";
import { h } from "./dom.js";
import { icon } from "./icons.js";

/** @typedef {"info" | "success" | "warning" | "error"} FeedbackTone */

let region = /** @type {HTMLElement | null} */ (null);
function toastRegion() {
  if (region?.isConnected) return region;
  region = h("div", {
    class: "ui-toasts",
    role: "status",
    aria: { live: "polite", atomic: "false" },
  });
  document.body.append(region);
  return region;
}

const toneIcon = {
  info: "info",
  success: "check",
  warning: "alert",
  error: "alert-circle",
};

/**
 * Brief confirmation (DS-11). One polite live region announces it; it dismisses
 * itself after five seconds unless it carries an action.
 *
 * @param {string} message
 * @param {{ tone?: FeedbackTone, action?: { text: string, onClick: () => void }, timeout?: number }} [options]
 */
export function toast(
  message,
  { tone = "success", action, timeout = action ? 10_000 : 5_000 } = {},
) {
  const host = toastRegion();
  const item = h(
    "div",
    { class: "ui-toast", dataset: { tone } },
    icon(toneIcon[tone]),
    h("span", { class: "ui-toast__message", text: message }),
    action &&
      button({
        text: action.text,
        variant: "link",
        size: "sm",
        onClick: () => {
          item.remove();
          action.onClick();
        },
      }),
    button({
      label: "Dismiss",
      icon: "x",
      variant: "ghost",
      size: "sm",
      onClick: () => item.remove(),
    }),
  );
  host.append(item);
  const timer = setTimeout(() => item.remove(), timeout);
  item.addEventListener("pointerenter", () => clearTimeout(timer), {
    once: true,
  });
  return item;
}

/**
 * Persistent page-level message (DS-11) with a cause and a next step.
 * @param {{ tone?: FeedbackTone, title: string, body?: string, action?: Node, onDismiss?: () => void }} options
 */
export function banner({ tone = "info", title, body, action, onDismiss }) {
  const element = h(
    "div",
    {
      class: "ui-banner",
      dataset: { tone },
      role: tone === "error" ? "alert" : "status",
    },
    h("span", { class: "ui-banner__icon" }, icon(toneIcon[tone])),
    h(
      "div",
      { class: "ui-banner__text" },
      h("p", { class: "ui-banner__title", text: title }),
      body && h("p", { class: "ui-banner__body", text: body }),
    ),
    action && h("div", { class: "ui-banner__action" }, action),
    onDismiss &&
      button({
        label: "Dismiss",
        icon: "x",
        variant: "ghost",
        size: "sm",
        onClick: () => {
          element.remove();
          onDismiss();
        },
      }),
  );
  return element;
}
